import { useEffect, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toPhoneInputValue } from '@/shared/utils/phone';

interface SignupPhoneVerificationProps {
  phone: string;
  code: string;
  phoneError?: string;
  codeError?: string;
  canRequestCode: boolean;
  // S4: SMS flow not wired yet. S5 will set `codeRequested` true after sendCode,
  // expose a `secondsLeft` countdown, and toggle the verified state.
  codeRequested: boolean;
  secondsLeft: number;
  phoneVerified: boolean;
  sending: boolean;
  verifying: boolean;
  onPhoneChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onPhoneBlur: () => void;
  onCodeBlur: () => void;
  onSendCode: () => void;
  onVerifyCode: () => void;
  disabled?: boolean;
}

function formatTimer(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const min = String(Math.floor(safe / 60)).padStart(2, '0');
  const sec = String(safe % 60).padStart(2, '0');
  return `${min}:${sec}`;
}

export const SignupPhoneVerification = ({
  phone,
  code,
  phoneError,
  codeError,
  canRequestCode,
  codeRequested,
  secondsLeft,
  phoneVerified,
  sending,
  verifying,
  onPhoneChange,
  onCodeChange,
  onPhoneBlur,
  onCodeBlur,
  onSendCode,
  onVerifyCode,
  disabled,
}: SignupPhoneVerificationProps) => {
  const { t } = useTranslation('auth');
  const codeInputRef = useRef<HTMLInputElement>(null);

  // SHCT-695 — 공통 00 §2.1 governs the input, not just the stored value, so the
  // field never holds a hyphen in the first place.
  const phoneChange = (e: ChangeEvent<HTMLInputElement>) =>
    onPhoneChange(toPhoneInputValue(e.target.value));
  const codeChange = (e: ChangeEvent<HTMLInputElement>) => onCodeChange(e.target.value);

  // Move focus to the code input the first time the verify row mounts
  // (i.e. right after a successful sendCode). Skip when re-mounted into the
  // already-verified state.
  useEffect(() => {
    if (codeRequested && !phoneVerified) codeInputRef.current?.focus();
  }, [codeRequested, phoneVerified]);

  const timerExpired = codeRequested && secondsLeft <= 0;
  // PRD 19 §8.1 — on local OTP timer expiry, surface the canonical inline
  // message (PRD 21 §5.4) without waiting on a verify call (the button is
  // disabled at 00:00). Expiry is the actionable state, so it takes priority
  // over any stale API codeError.
  const showExpiry = timerExpired && !phoneVerified;
  const codeMessage = showExpiry ? t('signup.errors.codeExpired') : codeError;

  return (
    <div className="signup-page__field">
      <label className="login-page__label" htmlFor="signupPhone">
        {t('signup.fields.phone')}
      </label>
      <div className="signup-page__row">
        <div className="login-page__input-wrap">
          <input
            id="signupPhone"
            type="tel"
            className={`login-page__input signup-page__input-no-icon${
              phoneError ? ' login-page__input--error' : ''
            }`}
            placeholder={t('signup.placeholders.phone')}
            inputMode="numeric"
            value={phone}
            onChange={phoneChange}
            onBlur={onPhoneBlur}
            disabled={disabled}
            aria-invalid={!!phoneError}
            aria-describedby={phoneError ? 'signupPhoneError' : undefined}
          />
        </div>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onSendCode}
          disabled={disabled || !canRequestCode || sending}
        >
          {t('signup.actions.sendCode')}
        </button>
      </div>
      {phoneError && (
        <p id="signupPhoneError" className="login-page__error" role="alert">
          {phoneError}
        </p>
      )}

      {codeRequested && (
        <div className="signup-page__verify-wrap">
          <div className="signup-page__verify-row">
            <div className="login-page__input-wrap">
              <input
                id="signupCode"
                ref={codeInputRef}
                type="text"
                className={`login-page__input signup-page__input-no-icon${
                  codeMessage ? ' login-page__input--error' : ''
                }`}
                placeholder={t('signup.placeholders.code')}
                inputMode="numeric"
                value={code}
                onChange={codeChange}
                onBlur={onCodeBlur}
                disabled={disabled || phoneVerified || timerExpired}
                aria-invalid={!!codeMessage}
                aria-describedby={codeMessage ? 'signupCodeError' : undefined}
              />
            </div>
            <span
              className="signup-page__timer"
              aria-live="off"
              aria-label={t('signup.fields.code')}
            >
              {phoneVerified ? t('signup.states.verifiedShort') : formatTimer(secondsLeft)}
            </span>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={onVerifyCode}
              disabled={
                disabled ||
                phoneVerified ||
                verifying ||
                timerExpired ||
                code.length !== 6
              }
            >
              {t('signup.actions.verifyCode')}
            </button>
          </div>
          {codeMessage && (
            <p id="signupCodeError" className="login-page__error" role="alert">
              {codeMessage}
            </p>
          )}
          {phoneVerified && (
            <p className="signup-page__verified" role="status">
              {t('signup.states.verified')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
